import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert, Progress } from 'antd';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric14,
  type DashboardMetric14Scope,
  type DashboardMetric14DesignCompletionBreakdown,
} from '@/services/dashboardService';

const { Text } = Typography;

export type EX1WalkthroughDesignCompletionStatusProgressBarProps = {
  height?: number;
  title?: string;
};

const clampPercent = (n: number) => {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

const toPercent = (
  r: DashboardMetric14DesignCompletionBreakdown | null | undefined,
) => {
  const v = r?.completionPercentage ?? 0;

  // If backend ever returns 0..1, normalize (safe guard).
  // If it’s already 0..100, this will keep it correct.
  const normalized = v <= 1 ? v * 100 : v;
  return clampPercent(normalized);
};

export const EX1WalkthroughDesignCompletionStatusProgressBar: React.FC<
  EX1WalkthroughDesignCompletionStatusProgressBarProps
> = ({
  height = 220,
  title = 'Walkthrough/Design completion status (%)',
}) => {
  // Get annualReportId from Redux (same pattern as your other dashboard components)
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [rawAll, setRawAll] = useState<
    Partial<
      Record<
        DashboardMetric14Scope,
        DashboardMetric14DesignCompletionBreakdown | null
      >
    >
  >({});

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRawAll({});
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const scopes: DashboardMetric14Scope[] = [
          'cycle',
          'process',
          'control',
        ];

        const results = await Promise.all(
          scopes.map(async s => {
            const r = await getDashboardMetric14(annualReportId, s);
            return [s, r ?? null] as const;
          }),
        );

        if (!isMounted) return;
        setRawAll(Object.fromEntries(results));
      } catch (err: any) {
        if (!isMounted) return;
        setRawAll({});
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 14 (Design completion).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const rows = useMemo(
    () => [
      { scope: 'cycle' as const, label: 'By Cycle' },
      { scope: 'process' as const, label: 'By Process' },
      { scope: 'control' as const, label: 'By Control' },
    ],
    [],
  );

  const isEmpty =
    !loading && !errorMessage && rows.every(r => !rawAll[r.scope]);

  return (
    <Card title={title}>
      <div
        style={{
          height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 14 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No design completion data was returned for this report.'
            showIcon
          />
        ) : (
          <div
            style={{
              padding: '6px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {rows.map(({ scope, label }) => {
              const p = toPercent(rawAll[scope]);

              return (
                <div key={scope}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <Text style={{ fontWeight: 600 }}>
                      Design Phase ({label})
                    </Text>
                    <Text>{p.toFixed(0)}%</Text>
                  </div>

                  <Progress percent={p} showInfo={false} />
                </div>
              );
            })}

            <div style={{ marginTop: 6, textAlign: 'center' }}>
              <Text type='secondary'>
                Completion = (Design Effectiveness == &quot;Yes&quot;) /
                (Total In-Scope Controls)
              </Text>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EX1WalkthroughDesignCompletionStatusProgressBar;
